/**
 * Chart.js dataset helpers for Project Overview Worker Hours / Concrete charts.
 */

import { CHART_COLORS, DATE_TIME_CONFIG } from "../../../../../config";
import {
  formatShortMonthDay,
  formatWeekOfLabel,
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  getWeekStart,
  toLocalDateKey,
} from "../../../../../utils/datetime";
import { withTypePrefix } from "../metricsHelpers";

/** Stacked regular hours (matches Overview tile styling). */
export const WORKER_HOURS_REGULAR_COLOR = CHART_COLORS.labor;
/** Stacked overtime hours (matches Overview overtime accent). */
export const WORKER_HOURS_OVERTIME_COLOR = CHART_COLORS.secondary;
/** Stacked double-time hours (top of bar, above overtime). */
export const WORKER_HOURS_DOUBLE_TIME_COLOR = CHART_COLORS.subcontractors;
/** Workers line on the secondary axis. */
export const WORKER_HOURS_WORKERS_COLOR = CHART_COLORS.primary;
/** Concrete volume bars (fallback / single-series). */
export const CONCRETE_VOLUME_COLOR = CHART_COLORS.primary;

/** Palette for stacked mix-design segments. */
const CONCRETE_MIX_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.labor,
  CHART_COLORS.subcontractors,
  "#71b93e",
  "#26cdc4",
  "#8b5cf6",
  "#a5a5a5",
];

const WEEK_START = DATE_TIME_CONFIG.START_OF_WEEK.default;

/** Chart bucket modes for Overview metrics charts. */
export const CHART_GRANULARITY_DAILY = "daily";
export const CHART_GRANULARITY_WEEKLY = "weekly";

/** Prefer Daily when All-range data would span fewer than this many weeks. */
const MIN_WEEKS_FOR_WEEKLY_DEFAULT = 8;

/**
 * Count distinct Sunday-start weeks represented in day rows.
 *
 * @param {Array<Object>|null|undefined} days
 * @returns {number}
 */
export function countOverviewChartWeeks(days) {
  const weekKeys = new Set();
  (days || []).forEach((day) => {
    if (!day?.date) return;
    const dayDate = getDateInLocalTZFromDateStr(day.date);
    weekKeys.add(toLocalDateKey(getWeekStart(dayDate, WEEK_START)));
  });
  return weekKeys.size;
}

/**
 * Default chart bucket: Weekly for All (when ≥ 8 weeks of data), else Daily.
 * Month ranges always default to Daily.
 *
 * @param {{start: string|null, end: string|null}|null} range
 * @param {Array<Object>|null|undefined} [days] - Aggregated day rows for the week-span check.
 * @returns {"daily"|"weekly"}
 */
export function getDefaultOverviewChartGranularity(range, days = null) {
  const isAllRange = !range?.start || !range?.end;
  if (!isAllRange) {
    return CHART_GRANULARITY_DAILY;
  }
  if (countOverviewChartWeeks(days) < MIN_WEEKS_FOR_WEEKLY_DEFAULT) {
    return CHART_GRANULARITY_DAILY;
  }
  return CHART_GRANULARITY_WEEKLY;
}

/**
 * Expand sparse day rows across an inclusive YYYY-MM-DD range (zero-fill).
 * When range has no bounds, return days as-is (activity days only).
 *
 * @param {Array<Object>} days - Aggregated day rows keyed by ``date``.
 * @param {{start: string|null, end: string|null}|null} range
 * @param {(dateKey: string) => Object} emptyDay - Factory for a zero day row.
 * @returns {Array<Object>}
 */
export function fillDaysForRange(days, range, emptyDay) {
  const byDate = Object.fromEntries(
    (days || []).map((day) => [day.date, day])
  );

  if (!range?.start || !range?.end) {
    return (days || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  }

  const filled = [];
  let cursor = getDateInLocalTZFromDateStr(range.start);
  const end = getDateInLocalTZFromDateStr(range.end);
  while (cursor.getTime() <= end.getTime()) {
    const dateKey = toLocalDateKey(cursor);
    filled.push(byDate[dateKey] || emptyDay(dateKey));
    cursor = getShiftedDate(cursor, 1);
  }
  return filled;
}

/**
 * Format a day key as a two-line Chart.js category label.
 *
 * Chart.js renders each array entry on its own line, e.g.:
 *   Mon
 *   Jul 20
 *
 * @param {string} dateKey - YYYY-MM-DD.
 * @returns {[string, string]}
 */
export function formatChartDayLabel(dateKey) {
  const date = getDateInLocalTZFromDateStr(dateKey);
  return [
    date.toLocaleDateString("en-US", { weekday: "short" }),
    formatShortMonthDay(date),
  ];
}

/**
 * Format a week-start key like the schedule week header.
 *
 * @param {string} weekStartKey - YYYY-MM-DD week start.
 * @returns {string}
 */
export function formatChartWeekLabel(weekStartKey) {
  return formatWeekOfLabel(weekStartKey);
}

/**
 * Shared Chart.js options for Overview metric charts.
 *
 * @param {Object} [options]
 * @param {string} [options.tooltipSuffix=""] - Suffix appended to tooltip values.
 * @param {boolean} [options.indexInteraction=false] - Enable index-mode tooltips.
 * @returns {Object}
 */
function getOverviewChartBaseOptions({
  tooltipSuffix = "",
  indexInteraction = false,
} = {}) {
  const config = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        callbacks: {
          // Two-line day labels → "Mon, Jul 20" (Chart.js default is "Mon,Jul 20").
          title(items) {
            const item = items?.[0];
            if (!item) return "";
            const raw = item.chart?.data?.labels?.[item.dataIndex];
            return Array.isArray(raw) ? raw.join(", ") : item.label;
          },
          label(context) {
            return `${context.dataset.label}: ${context.parsed.y}${tooltipSuffix}`;
          },
        },
      },
    },
  };

  if (indexInteraction) {
    config.interaction = {
      mode: "index",
      intersect: false,
    };
  }

  return config;
}

/** Shared stacked x-axis config for Overview charts. */
function getOverviewStackedXScale() {
  return {
    stacked: true,
    grid: { display: false },
    ticks: {
      maxRotation: 0,
      minRotation: 0,
      // Prefer a label on every category; Chart.js thins to every N when narrow.
      autoSkip: true,
      autoSkipPadding: 6,
      font: {
        size: 10,
      },
    },
  };
}

/** Shared stacked primary y-axis config for Overview charts. */
function getOverviewStackedYScale(title) {
  return {
    stacked: true,
    beginAtZero: true,
    title: {
      display: true,
      text: title,
    },
    grid: {
      color: "rgba(148, 163, 184, 0.35)",
    },
  };
}

/**
 * Roll day rows into Sunday-start week buckets for All-range charts.
 *
 * @param {Array<Object>} days
 * @param {"hours"|"concrete"} kind
 * @returns {Array<Object>}
 */
function aggregateDaysToWeeks(days, kind) {
  const weeks = {};

  (days || []).forEach((day) => {
    const dayDate = getDateInLocalTZFromDateStr(day.date);
    const weekStartKey = toLocalDateKey(getWeekStart(dayDate, WEEK_START));
    if (!weeks[weekStartKey]) {
      if (kind === "hours") {
        weeks[weekStartKey] = {
          date: weekStartKey,
          regularHours: 0,
          overtimeHours: 0,
          doubleTimeHours: 0,
          workerIds: new Set(),
        };
      } else {
        weeks[weekStartKey] = {
          date: weekStartKey,
          yards: 0,
          pourCount: 0,
          yardsByMix: {},
        };
      }
    }

    const bucket = weeks[weekStartKey];
    if (kind === "hours") {
      bucket.regularHours += Number(day.regularHours) || 0;
      bucket.overtimeHours += Number(day.overtimeHours) || 0;
      bucket.doubleTimeHours += Number(day.doubleTimeHours) || 0;
      (day.workerIds || []).forEach((id) => bucket.workerIds.add(id));
    } else {
      bucket.yards += Number(day.yards) || 0;
      bucket.pourCount += Number(day.pourCount) || 0;
      Object.entries(day.yardsByMix || {}).forEach(([mixKey, yards]) => {
        bucket.yardsByMix[mixKey] =
          (bucket.yardsByMix[mixKey] || 0) + (Number(yards) || 0);
      });
    }
  });

  return Object.keys(weeks)
    .sort()
    .map((key) => {
      const bucket = weeks[key];
      if (kind === "hours") {
        return {
          date: bucket.date,
          regularHours: bucket.regularHours,
          overtimeHours: bucket.overtimeHours,
          doubleTimeHours: bucket.doubleTimeHours,
          workerCount: bucket.workerIds.size,
        };
      }
      return {
        date: bucket.date,
        yards: bucket.yards,
        pourCount: bucket.pourCount,
        yardsByMix: bucket.yardsByMix,
      };
    });
}

/**
 * Build the visible series + label formatter for the active range / granularity.
 *
 * @param {Array<Object>|null|undefined} days
 * @param {{start: string|null, end: string|null}|null} range
 * @param {(dateKey: string) => Object} emptyDay
 * @param {"hours"|"concrete"} kind
 * @param {"daily"|"weekly"} granularity
 * @returns {{series: Array<Object>, formatLabel: (dateKey: string) => string, weekly: boolean}}
 */
function buildChartSeries(days, range, emptyDay, kind, granularity) {
  const daily = fillDaysForRange(days, range, emptyDay);
  const weekly = granularity === CHART_GRANULARITY_WEEKLY;
  if (!weekly) {
    return {
      series: daily,
      formatLabel: formatChartDayLabel,
      weekly: false,
    };
  }
  return {
    series: aggregateDaysToWeeks(daily, kind),
    formatLabel: formatChartWeekLabel,
    weekly: true,
  };
}

/**
 * Build Chart.js data for Worker Hours (stacked RT/OT/DT bars + workers line).
 *
 * Stack order is RT (bottom) → OT → DT (top), matching timesheet buckets.
 *
 * @param {Array<Object>|null|undefined} days - Aggregated day rows.
 * @param {{start: string|null, end: string|null}|null} range
 * @param {"daily"|"weekly"} [granularity] - Bucket size (defaults from range).
 * @param {string} [typeLabel="All"] - Active Type name for dataset labels.
 * @returns {{labels: string[], datasets: Array<Object>, weekly: boolean}}
 */
export function buildWorkerHoursChartData(
  days,
  range = null,
  granularity = getDefaultOverviewChartGranularity(range),
  typeLabel = "All"
) {
  const { series, formatLabel, weekly } = buildChartSeries(
    days,
    range,
    (dateKey) => ({
      date: dateKey,
      regularHours: 0,
      overtimeHours: 0,
      doubleTimeHours: 0,
      workerCount: 0,
      workerIds: [],
    }),
    "hours",
    granularity
  );

  return {
    weekly,
    labels: series.map((day) => formatLabel(day.date)),
    datasets: [
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Regular Time"),
        data: series.map((day) => Number(day.regularHours) || 0),
        backgroundColor: WORKER_HOURS_REGULAR_COLOR,
        borderColor: WORKER_HOURS_REGULAR_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Overtime"),
        data: series.map((day) => Number(day.overtimeHours) || 0),
        backgroundColor: WORKER_HOURS_OVERTIME_COLOR,
        borderColor: WORKER_HOURS_OVERTIME_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Double Time"),
        data: series.map((day) => Number(day.doubleTimeHours) || 0),
        backgroundColor: WORKER_HOURS_DOUBLE_TIME_COLOR,
        borderColor: WORKER_HOURS_DOUBLE_TIME_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "line",
        label: withTypePrefix(typeLabel, "Workers"),
        data: series.map((day) => Number(day.workerCount) || 0),
        borderColor: WORKER_HOURS_WORKERS_COLOR,
        backgroundColor: WORKER_HOURS_WORKERS_COLOR,
        yAxisID: "y1",
        tension: 0.25,
        pointRadius: weekly ? 2 : 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        order: 1,
      },
    ],
  };
}

/**
 * Chart.js options for the Worker Hours dual-axis chart.
 *
 * @returns {Object}
 */
export function getWorkerHoursChartOptions() {
  return {
    ...getOverviewChartBaseOptions({ indexInteraction: true }),
    scales: {
      x: getOverviewStackedXScale(),
      y: {
        ...getOverviewStackedYScale("Hours"),
        position: "left",
      },
      y1: {
        position: "right",
        beginAtZero: true,
        grace: "10%",
        title: {
          display: true,
          text: "Workers",
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          precision: 0,
          stepSize: 1,
        },
      },
    },
  };
}

/**
 * Collect mix-design keys present in a concrete day series.
 *
 * @param {Array<Object>} series
 * @returns {string[]}
 */
function collectConcreteMixKeys(series) {
  const keys = new Set();
  (series || []).forEach((day) => {
    Object.keys(day.yardsByMix || {}).forEach((mixKey) => keys.add(mixKey));
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

/**
 * Build Chart.js data for Concrete volume bars stacked by mix design.
 *
 * @param {Array<Object>|null|undefined} days - Aggregated day rows.
 * @param {{start: string|null, end: string|null}|null} range
 * @param {"daily"|"weekly"} [granularity] - Bucket size (defaults from range).
 * @param {string} [typeLabel="All"] - Active Type name for dataset labels.
 * @returns {{labels: string[], datasets: Array<Object>, weekly: boolean}}
 */
export function buildConcretePoursChartData(
  days,
  range = null,
  granularity = getDefaultOverviewChartGranularity(range),
  typeLabel = "All"
) {
  const { series, formatLabel, weekly } = buildChartSeries(
    days,
    range,
    (dateKey) => ({
      date: dateKey,
      yards: 0,
      pourCount: 0,
      yardsByMix: {},
    }),
    "concrete",
    granularity
  );

  const mixKeys = collectConcreteMixKeys(series);
  const datasets =
    mixKeys.length > 0
      ? mixKeys.map((mixKey, index) => {
          const color = CONCRETE_MIX_COLORS[index % CONCRETE_MIX_COLORS.length];
          return {
            label: withTypePrefix(typeLabel, mixKey),
            data: series.map(
              (day) => Number(day.yardsByMix?.[mixKey]) || 0
            ),
            backgroundColor: color,
            borderColor: color,
            borderRadius: 2,
            stack: "volume",
          };
        })
      : [
          {
            label: withTypePrefix(typeLabel, "Volume (CY)"),
            data: series.map((day) => Number(day.yards) || 0),
            backgroundColor: CONCRETE_VOLUME_COLOR,
            borderColor: CONCRETE_VOLUME_COLOR,
            borderRadius: 2,
            stack: "volume",
          },
        ];

  return {
    weekly,
    labels: series.map((day) => formatLabel(day.date)),
    datasets,
  };
}

/**
 * Chart.js options for the Concrete volume bar chart.
 *
 * @returns {Object}
 */
export function getConcretePoursChartOptions() {
  return {
    ...getOverviewChartBaseOptions({ tooltipSuffix: " CY" }),
    scales: {
      x: getOverviewStackedXScale(),
      y: getOverviewStackedYScale("Cubic yards"),
    },
  };
}
